import { Injectable, inject } from "@angular/core";
import { ReasoningStepService } from "./reasoning-step.service";
import type { ReasoningStepInfo } from "./reasoning-step.service";
import type { BlackboardIndicator, BlackboardPhase, BlackboardTaskRow, ResolvedToolCall, SubAgentTodo } from "../model/block.types";
import type { WorkerToolCall } from "./worker-todo-stream.service";

export type { ResolvedToolCall };

export type MessageItem =
  | { kind: "user"; message: any }
  | { kind: "assistant"; message: any; toolCalls: ResolvedToolCall[] }
  | { kind: "reasoning"; id: string; stepInfo: ReasoningStepInfo }
  | { kind: "todo_list"; id: string; todos: SubAgentTodo[]; toolCall: WorkerToolCall | null; label: string | null }
  | {
      kind: "blackboard";
      id: string;
      indicators: Partial<Record<BlackboardPhase, BlackboardIndicator>>;
      taskRows: BlackboardTaskRow[];
    }
  | { kind: "pending" };

/**
 * Classifies a raw AG-UI `messages` array into renderable `MessageItem`s (user bubble,
 * assistant bubble, reasoning step, or pending placeholder). Pure with respect to its
 * input — extracted from ChatMessageListComponent so any message thread (main chat or a
 * sub-agent's own thread) can be classified the same way without depending on
 * chat-container-scoped state. Live/streaming extras (e.g. WorkerReasoningStreamService
 * steps) are woven in by the caller on top of the returned array, not here.
 */
@Injectable({ providedIn: "root" })
export class MessageClassifierService {
  private readonly reasoningStepService = inject(ReasoningStepService);

  classify(messages: any[]): MessageItem[] {
    const all = messages;

    // Build lookup: toolCallId → function name for labelling tool result steps
    const toolNameByCallId = new Map<string, string>();
    for (const m of all) {
      if (m.role === "assistant" && Array.isArray(m.toolCalls)) {
        for (const tc of m.toolCalls) {
          if (tc.id && tc.function?.name) toolNameByCallId.set(tc.id, tc.function.name);
        }
      }
    }

    // Build set of tool call IDs that have received a result
    const toolResultIds = new Set<string>(
      all.filter((m: any) => m.role === "tool" && m.toolCallId).map((m: any) => m.toolCallId as string),
    );

    // Classify each message into a renderable item
    const items: MessageItem[] = [];
    for (const m of all) {
      if (m.role === "tool") {
        const toolName = toolNameByCallId.get(m.toolCallId) ?? "Tool";
        items.push({ kind: "reasoning", id: m.id, stepInfo: { stepLabel: toolName, description: "" } });
        continue;
      }

      if (m.role !== "assistant") {
        items.push({ kind: "user", message: m });
        continue;
      }

      const cls = this.reasoningStepService.classifyMessage(m.content);
      if (cls.type === "message") {
        const toolCalls: ResolvedToolCall[] = Array.isArray(m.toolCalls)
          ? m.toolCalls
              .filter((tc: any) => tc.id && tc.function?.name)
              .map((tc: any) => ({
                id: tc.id as string,
                name: tc.function.name as string,
                hasResult: toolResultIds.has(tc.id),
              }))
          : [];
        items.push({ kind: "assistant", message: m, toolCalls });
      } else if (cls.type === "reasoning") {
        items.push({ kind: "reasoning", id: m.id, stepInfo: cls.info });
      } else if (cls.type === "pending") {
        items.push({ kind: "pending" });
      }
      // silent → omit
    }

    return items;
  }
}
